<template>
  <div>
    <h2 v-if="!inline">
      {{ assignedType }}
      <button v-if="canUpdate && !editData"
        type="button"
        class="btn-icon"
        :aria-label="`Edit ${assignedType}`"
        title="Click to change value"
        @click="editData = true">
        <i class="fas fa-edit" aria-hidden="true"></i>
      </button>
      <template v-else-if="canUpdate && editData">
        <button type="button"
          class="btn-icon"
          aria-label="Save changes"
          title="Click to save changes"
          @click="emitChanges">
          <i class="fas fa-check" aria-hidden="true"></i>
        </button>
        <button type="button"
          class="btn-icon"
          aria-label="Cancel edit"
          title="Click to cancel edit"
          @click="editData = false">
          <i class="fas fa-times-circle" aria-hidden="true"></i>
        </button>
      </template>
    </h2>
    <div class="update-options" v-if="editData">
      <span v-if="inline">{{ assignedType }}: </span>
      <k-dropdown v-if="canUpdate && showDropdown"
        v-model="newUserInfo"
        :options="options"
        :showSearch="showSearch"
        :clear-option="assignedType == 'coach' || assignedType == 'data mentor' ? true : false"
      >
      </k-dropdown>
      <k-date-picker v-if="canUpdate && !showDropdown"
        v-model="newUserInfo"
        type="date"
      >
      </k-date-picker>
      <template v-if="inline && canUpdate">
        <button type="button"
          class="btn-icon"
          aria-label="Save changes"
          title="Click to save changes"
          @click="emitChanges">
          <i class="fas fa-check" aria-hidden="true"></i>
        </button>
        <button type="button"
          class="btn-icon"
          aria-label="Cancel edit"
          title="Click to cancel edit"
          @click="cancelEdit">
          <i class="fas fa-times-circle" aria-hidden="true"></i>
        </button>
      </template>
    </div>
    <div class="update-options" v-else>
      <span v-if="inline">{{ assignedType }}: </span>
      <div class="metadata-value">
        <div v-if="mentor" class="mentor-name-container">
          <router-link v-if="$permissions.hasPermission('view_all_learners')" :to="{ name: 'dashboard_students', params: { studentId: mentor.user_id } }">
            <b>{{ mentor.name }}</b><i class="fas fa-external-link-alt" aria-hidden="true"></i>
          </router-link>
          <p v-else class="mentor-name">{{ mentor.name }}</p>
          <p>{{ mentor.email }}</p>
        </div>
        <div v-else-if="employer">
          <b>{{ employer.name }}</b>
        </div>
        <div v-else-if="estimatedGraduationDate">
          <b>{{ parseTimestamp(estimatedGraduationDate, undefined, "do MMM yyyy") }}</b>
        </div>
        <div v-else-if="estimatedGraduationDate === undefined || estimatedGraduationDate === null">
          <span>No {{ assignedType }} assigned</span>
        </div>
        <button v-if="inline && canUpdate"
          type="button"
          class="btn-icon"
          :aria-label="`Edit ${assignedType}`"
          title="Click to change value"
          @click="editData = true">
          <i class="fas fa-edit" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
h2 {
  text-transform: capitalize;
  display: flex;
  gap: 15px;
}

i.fas.fa-check {
  padding-right: 10px;
}

.update-options {
  display: flex;
  gap: 10px;
  align-items: center;
}

.metadata-value {
  display: flex;
  gap: 5px;
  align-items: center;
  min-width: 0;
}

.update-options .k-search-dropdown-menu {
  min-width: 200px !important;
}
</style>

<script>
import useGlobalStore from '@stores/global';
import KDropdown from '@base-components/k-dropdown.vue';
import KDatePicker from '@base-components/k-date-picker.vue';
import TimeMixin from '@mixins/time-mixins';

export default {
  components: {
    'k-dropdown': KDropdown,
    'k-date-picker': KDatePicker,
  },

  mixins: [TimeMixin],

  props: {
    assignedType: {
      type: String,
    },
    mentor: {
      type: Object,
    },
    employer: {
      type: Object,
    },
    estimatedGraduationDate: {
      type: [Date, String],
    },
    options: {
      type: Array,
    },
    canUpdate: {
      type: Boolean,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      store: useGlobalStore(),
      editData: false,
      newUserInfo: this.initialiseDefaultUserInfo(),
    };
  },

  watch: {
    mentor() {
      this.newUserInfo = this.mentor ? this.mentor.id : null;
    },
    employer() {
      this.newUserInfo = this.employer ? this.employer.id : null;
    },
  },

  computed: {
    showDropdown() {
      return Boolean(this.options);
    },
  },

  methods: {
    emitChanges() {
      if (this.canUpdate) {
        this.$emit('new-data', this.newUserInfo);
        this.editData = false;
      }
    },

    cancelEdit() {
      this.newUserInfo = this.initialiseDefaultUserInfo();
      this.editData = !this.editData;
    },

    initialiseDefaultUserInfo() {
      if (this.mentor) {
        return this.mentor.user_id;
      } if (this.employer) {
        return this.employer.employer_id;
      } if (this.estimatedGraduationDate) {
        return this.estimatedGraduationDate;
      }
      return undefined;
    },
  },
};
</script>
